/* eslint-disable showpass-custom/no-styled-components */
import Divider from "@ui-kit/data-display/Divider/Divider.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import styled from "@emotion/styled";
import { NODE_ENV } from "@shared/env";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { isPlatform } from "@shared/utils/get-platform";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import { useTranslation } from "@shared/utils/internationalization";
import url from "@shared/utils/urls/url";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import CDNImage from "@ui-kit/media/CDNImage/CDNImage.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Cookies from "js-cookie";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { Fragment } from "react";
import { NavBarMobileModalProps } from "./NavBarMobileModal.props";

const ProfileImage = styled(CDNImage)`
  user-select: none;
  border-radius: 50px;
  border: 2px solid red;
`;

const LinkWrapper = styled(Box)`
  &:not(:last-of-type) {
    margin-bottom: 5px;
  }
`;

const LocaleWrapper = styled.div`
  display: flex;

  &:not(:last-of-type) {
    margin-bottom: 5px;
  }
`;

const GrowContainer = styled(Box)`
  flex-grow: 1;
  margin-bottom: 30px;
`;

const StyledButton = styled(Button)`
  padding-left: 60px;
  padding-right: 60px;
  margin-left: 30px;
  margin-bottom: 31px;
`;

const StyledTextButton = styled(Button.Nav)`
  padding: 0 0 0 30px;
  width: 100%;
  height: 54px;
  justify-content: flex-start;
  font-size: 1rem;
`;

const StyledDivider = styled(Divider)`
  border-color: var(--clr-paint-gray-400);
  margin: 20px 0;
  opacity: 1;
`;

const NavBarMobileModal = ({
  onClose,
  isOpen,
  loggedInLinks,
  loggedOutLinks,
  locales,
  goToLocale,
  logInOnClick,
  signUpOnClick,
  handleLogout,
}: NavBarMobileModalProps) => {
  const router = useRouter();
  const { t } = useTranslation("common");
  const { isLoggedIn, userProfileData: user } = useUserSessionAuthState();

  const callAndHandleClose = (callback: () => void) => {
    if (callback) {
      callback();
    }
    onClose();
  };

  return (
    <Modal onClose={onClose} size="full" isOpen={isOpen}>
      <Modal.Body p="75px 0 52px" display="flex" flexDirection="column">
        <Modal.CloseButton color="font.dark" />
        {isLoggedIn ? (
          <GrowContainer>
            {user && (
              <>
                <Flex alignItems="center" ml="xl" mb="2xl">
                  <>
                    <ProfileImage
                      width={50}
                      height={50}
                      alt={t("User profile image")}
                      src={
                        user.profile_picture ||
                        formatRelativeStaticPath("images/no-avatar.jpg")
                      }
                    />
                    <Box width="element.xs" />
                    <Text fontSize="xl" data-sentry-mask>
                      {user.username}
                    </Text>
                  </>
                </Flex>
                {!isPlatform("desktop") &&
                  Boolean(Cookies.get("showpass-venue-id")) && (
                    <NextLink href={url("dashboard")}>
                      <StyledTextButton
                        data-testid="navbar-mobile-modal-dashboard"
                        onClick={onClose}
                      >
                        {t("Dashboard")}
                      </StyledTextButton>
                    </NextLink>
                  )}
                {/** this button is for development/testing convenience; button links straight to the new manage dashboard */}
                {Boolean(Cookies.get("showpass-venue-id")) &&
                  (NODE_ENV === "local" || NODE_ENV === "preview") && (
                    <NextLink href={url("manage")}>
                      <StyledTextButton
                        data-testid="navbar-mobile-modal-manage"
                        onClick={onClose}
                      >
                        {t("Manage")}
                      </StyledTextButton>
                    </NextLink>
                  )}
              </>
            )}
            <Box h="element.2xs" />
            {loggedInLinks.map((link) => {
              return (
                <Fragment key={link.href}>
                  <NextLink href={link.href} passHref>
                    <StyledTextButton
                      onClick={onClose}
                      data-testid={`navbar-mobile-modal-${link.label}-button`}
                    >
                      {link.label}
                    </StyledTextButton>
                  </NextLink>
                  <Box h="element.2xs" />
                </Fragment>
              );
            })}
            <StyledTextButton
              data-testid="navbar-mobile-modal-logout"
              onClick={() => callAndHandleClose(handleLogout)}
              color="var(--clr-paint-secondary-600) !important"
            >
              {t("Log Out")}
            </StyledTextButton>
          </GrowContainer>
        ) : (
          <GrowContainer>
            <StyledButton
              data-testid="navbar-mobile-modal-login-button"
              size="lg"
              variant="outline"
              colorPalette="primary"
              onClick={() => callAndHandleClose(logInOnClick)}
            >
              {t("Log In")}
            </StyledButton>
            {!isPlatform("desktop") && (
              <StyledTextButton
                data-testid="navbar-mobile-modal-create-account-button"
                onClick={signUpOnClick}
              >
                {t("Create Account")}
              </StyledTextButton>
            )}
            <Box pl={30} pr={30}>
              <StyledDivider />
            </Box>
            {loggedOutLinks?.map((link) => {
              return (
                <LinkWrapper key={link.label}>
                  <NextLink href={link.href}>
                    <StyledTextButton
                      onClick={onClose}
                      data-testid={`navbar-mobile-modal-${link.label}-button`}
                    >
                      {link.label}
                    </StyledTextButton>
                  </NextLink>
                </LinkWrapper>
              );
            })}
          </GrowContainer>
        )}
      </Modal.Body>

      <Modal.Footer>
        {Object.keys(locales).map((locale) => {
          const { label: optionLabel } = locales[locale];
          return (
            <LocaleWrapper key={locale} onClick={() => goToLocale(locale)}>
              <Button.Nav
                data-testid={`navbar-mobile-modal-${locale}-locale-button`}
                pl={30}
                width="100%"
                justifyContent="flex-start"
                height="element.4xl"
                color={router.locale === locale ? "font.dark" : "gray.500"}
                _focus={{
                  backgroundColor: "gray.300",
                }}
                _hover={{
                  backgroundColor: "gray.300",
                  color: router.locale === locale ? "font.dark" : "gray.500",
                }}
              >
                {optionLabel}
              </Button.Nav>
            </LocaleWrapper>
          );
        })}
      </Modal.Footer>
    </Modal>
  );
};
export default NavBarMobileModal;
